/**
 * WorkspaceHome — Homepage with Hero + scenario card grid.
 *
 * Clicking the Hero CTA or any card prefills the chat input without sending,
 * letting the user review and decide when to submit.
 */

import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatPanel } from '../../context/ChatPanelContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useAgent } from '../../context/AgentContext';
import { useLocale } from '../../i18n/useLocale';
import './WorkspaceHome.css';
import {
  homepageConfigAgent,
  isAllowedHref,
  loadHomepageConfig,
  localizeHomepage,
  resolveIconSrc,
  type HomepageConfig,
} from './homepageConfig';

// ── Corner links ────────────────────────────────────────────────────────────

/** SharePoint doc of ready-to-copy, scenario-organised prompts. Opens in a new
 *  tab, same as the agent selection guide link beside it — neither downloads. */
const PROMPT_COLLECTION_URL =
  'https://amazon-my.sharepoint.com/:u:/p/qiuyac/IQBSHOKNWDBSTrI_h44L0q58AUCvu5HtJihDDN87fbnodOU?e=1QTgjV';


// ── Component ───────────────────────────────────────────────────────────────

interface WorkspaceHomeProps {
  /** Test/override hook; when omitted the config is loaded via loadHomepageConfig. */
  configOverride?: HomepageConfig;
}

const BUSY_TIPS = ['home.busy1', 'home.busy2', 'home.busy3', 'home.busy4'] as const;

export default function ConfigDrivenHome({ configOverride }: WorkspaceHomeProps = {}) {
  const { t } = useTranslation('workspace');
  const { sendPrompt, chatQueueFull } = useChatPanel();
  const { isImpersonating } = useImpersonation();
  const navigate = useNavigate();
  const [busyToast, setBusyToast] = useState<string | null>(null);
  const busyToastTimer = useRef<ReturnType<typeof setTimeout>>();

  // Homepage content is config-driven, per agent: the user's selected agent maps
  // to the config the RBMS editor writes. Switching agent clears the current
  // config (no flash of the previous agent's page) and reloads; `cancelled`
  // drops a response that lands after the user has already switched again.
  const { currentAgent } = useAgent();
  const configAgent = homepageConfigAgent(currentAgent);
  const [config, setConfig] = useState<HomepageConfig | null>(configOverride ?? null);
  useEffect(() => {
    if (configOverride) return;
    let cancelled = false;
    setConfig(null);
    loadHomepageConfig(configAgent).then((c) => {
      if (!cancelled) setConfig(c);
    });
    return () => {
      cancelled = true;
    };
  }, [configOverride, configAgent]);
  // The admin-written content in the UI locale (English text where the admin gave it).
  const { locale } = useLocale();
  const view = useMemo(() => (config ? localizeHomepage(config, locale) : null), [config, locale]);

  const showBusyToast = useCallback((msg?: string) => {
    const tip = msg ?? t(BUSY_TIPS[Math.floor(Math.random() * BUSY_TIPS.length)]);
    clearTimeout(busyToastTimer.current);
    setBusyToast(tip);
    busyToastTimer.current = setTimeout(() => setBusyToast(null), 2500);
  }, [t]);

  const flyToInput = useCallback((e: React.SyntheticEvent<HTMLElement>, prompt: string, variant: 'card' | 'hero' = 'card') => {
    if (isImpersonating) {
      showBusyToast(t('home.readOnly'));
      return;
    }
    // Refuse before any animation if the queue is at capacity, so we
    // don't fly the ghost into the input only to have ChatPanel reject it.
    if (chatQueueFull) {
      showBusyToast(t('home.queueFull'));
      return;
    }

    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();

    // Create ghost immediately from the clicked element
    const ghost = document.createElement('div');
    ghost.className = variant === 'hero' ? 'ws-home__hero-ghost' : 'ws-home__card-ghost';
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${rect.top}px`;
    ghost.textContent = variant === 'hero' ? el.textContent : '';
    if (variant === 'card') {
      Array.from(el.childNodes).forEach(child => ghost.appendChild(child.cloneNode(true)));
    }
    document.body.appendChild(ghost);

    const glowClass = variant === 'hero' ? 'chat-panel__textarea--glow-teal' : 'chat-panel__textarea--glow';

    const animateGhost = (textarea: Element) => {
      const targetRect = textarea.getBoundingClientRect();
      const targetX = targetRect.left + targetRect.width / 2 - rect.width / 2;
      const targetY = targetRect.top + targetRect.height / 2 - rect.height / 2;
      const dx = targetX - rect.left;
      const dy = targetY - rect.top;

      requestAnimationFrame(() => {
        ghost.style.transform = `translate(${dx}px, ${dy}px) scale(0.15)`;
        ghost.style.opacity = '0';
      });

      const cleanup = setTimeout(() => ghost.remove(), 1000);
      ghost.addEventListener('transitionend', () => {
        clearTimeout(cleanup);
        ghost.remove();
        textarea.classList.add(glowClass);
        setTimeout(() => textarea.classList.remove(glowClass), 600);
      }, { once: true });
    };

    // Send directly: handleSend auto-sends when idle, enqueues when streaming.
    sendPrompt(prompt);

    // If textarea is already visible, animate immediately
    const textarea = document.querySelector('.chat-panel__textarea');
    if (textarea && textarea.getBoundingClientRect().width > 0) {
      animateGhost(textarea);
    } else {
      // Panel was collapsed — wait for textarea to appear in the DOM
      const observer = new MutationObserver(() => {
        const ta = document.querySelector('.chat-panel__textarea');
        if (ta && ta.getBoundingClientRect().width > 0) {
          observer.disconnect();
          clearTimeout(safetyTimer);
          animateGhost(ta);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      const safetyTimer = setTimeout(() => { observer.disconnect(); ghost.remove(); }, 1000);
    }
  }, [sendPrompt, chatQueueFull, isImpersonating, showBusyToast, t]);

  return (
    <div className="ws-home">
      {/* Unstyled on desktop (the links pin to .ws-home's corners); on phones it
          lays the two links out side by side on one row. */}
      <div className="ws-home__corner-links">
        <a
          className="ws-home__corner-link ws-home__prompts-link"
          href={PROMPT_COLLECTION_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('home.promptsLink')}
        </a>
        <a
          className="ws-home__corner-link ws-home__guide-link"
          href="/docs/CMHK-AI-Agent-Guide.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('home.guideLink')}
        </a>
      </div>
      {/* Part 1 — Hero (skipped when the config has no part1) */}
      {view?.part1 &&
        (view.part1.titleSegments?.length ||
          view.part1.title ||
          view.part1.subheadingSegments?.length ||
          view.part1.subheading) && (
          <section className="ws-home__hero">
            <h1 className="ws-home__title">
              {view.part1.titleSegments?.length
                ? view.part1.titleSegments.map((seg, i) => (
                    <span key={i} style={seg.color ? { color: seg.color } : undefined}>
                      {seg.text}
                    </span>
                  ))
                : view.part1.title}
            </h1>
            {(view.part1.subheadingSegments?.length || view.part1.subheading) && (
              <p className="ws-home__subtitle">
                {view.part1.subheadingSegments?.length
                  ? view.part1.subheadingSegments.map((seg, i) => (
                      <span key={i} style={seg.color ? { color: seg.color } : undefined}>
                        {seg.text}
                      </span>
                    ))
                  : view.part1.subheading}
              </p>
            )}
          </section>
        )}

      {/* Part 2 — Hot question chips (skipped when empty) */}
      {view?.part2?.length ? (
        <div className="ws-home__chips">
          {view.part2.map((q) => {
            const tint = q.badges?.includes('NEW')
              ? ' ws-home__chip--new'
              : q.badges?.includes('HOT')
                ? ' ws-home__chip--hot'
                : '';
            return (
              <button key={q.id} className={`ws-home__chip${tint}`} onClick={(e) => flyToInput(e, q.prompt)}>
                {q.badges?.includes('NEW') && (
                  <span className="ws-home__chip-badge ws-home__chip-badge--new">{t('home.badgeNew')}</span>
                )}
                {q.badges?.includes('HOT') && (
                  <span className="ws-home__chip-badge ws-home__chip-badge--hot">{t('home.badgeHot')}</span>
                )}
                {q.uiCopy}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Part 3 — Topic cards (skipped when empty) */}
      {view?.part3?.length ? (
        <div className="ws-home__grid">
          {view.part3.map((card) => {
            const handleClick = (e: React.SyntheticEvent<HTMLElement>) => {
              if (card.action === 'navigate' && isAllowedHref(card.href)) {
                // Site paths stay in the SPA; external http(s) links open in a new tab.
                if (card.href.startsWith('/')) navigate(card.href);
                else window.open(card.href, '_blank', 'noopener,noreferrer');
                return;
              }
              if (card.prompt) flyToInput(e, card.prompt);
            };
            return (
              <div
                key={card.id}
                className="ws-home__card"
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e); } }}
              >
                {card.iconUrl && (
                  <div className="ws-home__card-icon">
                    <img src={resolveIconSrc(card.iconUrl)} alt={card.title} width="48" height="48" />
                  </div>
                )}
                <div className="ws-home__card-head">
                  <span className="ws-home__headline">
                    {card.badges?.includes('NEW') && <span className="ws-home__cardbadge ws-home__cardbadge--new">{t('home.badgeNew')}</span>}
                    {card.badges?.includes('HOT') && <span className="ws-home__cardbadge ws-home__cardbadge--hot">{t('home.badgeHot')}</span>}
                    <h2 className="ws-home__card-title">{card.title}</h2>
                  </span>
                </div>
                <p className="ws-home__card-desc">{card.description}</p>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Busy toast */}
      {busyToast && (
        <div className="ws-home__busy-toast">{busyToast}</div>
      )}
    </div>
  );
}
